import { IRynReplayMaskingPolicy } from '@ups/xplat/base/ryn-replay';
import { RynReplayRecordFn } from './ryn-replay-recorder.rrweb-backend.types';

/**
 * Build the rrweb `record()` options that come from the masking policy and
 * recorder config, everything EXCEPT `emit` (which needs the backend's own
 * `this.onRrwebEvent` closure, so it stays in
 * `ryn-replay-recorder.rrweb-backend.ts`).
 *
 * Split out (PR 3639 review) so the backend file's line count does not grow
 * every time a new masking-related record() option is added, put it here,
 * not in `attachRecorder`.
 *
 * - `maskAllInputs` / `blockClass` come straight from the policy.
 * - `maskTextSelector`: PR 3639 design-major fix. `maskAllInputs` only ever
 *   covered typed input values, never rendered text, and rrweb's `record()`
 *   had no maskText* option wired at all. `'*'` masks every text node;
 *   `undefined` (rrweb's own default) leaves an app's recording unchanged
 *   unless its policy explicitly turns `maskAllText` on. Per-app opt-in only,
 *   never a global default flip.
 * - `maskTextClass`: WI 34326. Always passed, so any app can mask one
 *   element's text with `ryn-replay-mask` and keep its layout in the replay.
 * - `collectFonts`/`inlineStylesheet`: inline stylesheets so CSS-heavy apps
 *   (Safety/HSE) replay with real layout, since external `<link>` hrefs fail
 *   inside the sandboxed replayer. Fonts stay off (bloat, little layout
 *   value); oversized snapshots still go through `fitRynReplayBatchesForIngest`
 *   (data-URL strip -> omit).
 * - `slimDOMOptions: 'all'`: drops script/comment/head chrome out of every
 *   snapshot so dense Kendo pages pay less main-thread serialize cost
 *   (#31398 ladder step 2). Application UI is unchanged; use `blockClass` for
 *   subtrees that must not be walked at all (e.g. Equipment Usage grid).
 * - `blockSelector`: PR 3639 review follow-up. `kendo-numerictextbox` mirrors
 *   its bound value onto the host element's `aria-valuenow`, an attribute
 *   neither `maskTextSelector` (text nodes) nor `maskAllInputs` (the native
 *   `<input>` value) reaches, so a pay-rate control could leak via its ARIA
 *   attribute even with full-text masking on. Only added for policies that
 *   already opted into `maskAllText` (the same per-app opt-in as
 *   `maskTextSelector`), combined with `blockClass` by OR, never a
 *   replacement for it. `policy.blockSelector` always carries the
 *   `kendo-numerictextbox` baseline (config can only append to it, never
 *   replace it, see `buildRynReplayMaskingPolicy`).
 */
export const buildRrwebRecordOptions = (
  policy: IRynReplayMaskingPolicy,
  recordCanvas: boolean
): Omit<Parameters<RynReplayRecordFn>[0], 'emit'> => ({
  maskAllInputs: policy.maskAllInputs,
  maskTextSelector: policy.maskAllText ? '*' : undefined,
  maskTextClass: policy.maskTextClass,
  blockClass: policy.blockClass,
  blockSelector: policy.maskAllText ? policy.blockSelector : undefined,
  recordCanvas,
  collectFonts: false,
  inlineStylesheet: true,
  slimDOMOptions: 'all',
});
